import { digObject } from '@domain-group/javascript-utils'
import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as artworkReviewActions from '@redux/modules/artworkReview'

import defaultFormState, { requiredFields } from '@models/artworkReview'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { ArtworkReviewModel, ArtworkReviewRequestOptions } from '@models/types'

export const generateUrls = (artworkReview?: Partial<ArtworkReviewModel>) => {
  const { id } = artworkReview || {}

  return {
    editArtworkReviewDetailsUrl: `#/systemManager/artworkReviews/${id}/edit/details`,
    editArtworkReviewUrl: `#/systemManager/artworkReviews/${id}/edit/`,
    artworkReviewsIndexUrl: '#/systemManager/artworkReviews',
  }
}

type CreateArtworkReviewParams = {
  artworkReviewParams: Partial<ArtworkReviewModel>,
  dispatch: AppDispatch,
  requestOptions?: ArtworkReviewRequestOptions,
}

const createArtworkReview = (params: CreateArtworkReviewParams) => {
  const { dispatch, artworkReviewParams, requestOptions } = params
  const { createArtworkReview: createFn } = artworkReviewActions

  return dispatch(createFn(artworkReviewParams, requestOptions))
}

type UpdateArtworkReviewParams = {
  artworkReview: ArtworkReviewModel,
  artworkReviewParams: Partial<ArtworkReviewModel>,
  dispatch: AppDispatch,
  requestOptions?: ArtworkReviewRequestOptions,
}

const updateArtworkReview = (params: UpdateArtworkReviewParams) => {
  const {
    dispatch, artworkReview, artworkReviewParams, requestOptions,
  } = params
  const { updateArtworkReview: updateFn } = artworkReviewActions

  const updatedParams = {
    id: artworkReview.id,
    ...artworkReviewParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteArtworkReviewParams = {
  artworkReview: DeleteParams<ArtworkReviewModel>,
  dispatch: AppDispatch,
}

const deleteArtworkReview = (params: DeleteArtworkReviewParams) => {
  const { dispatch, artworkReview } = params
  const { deleteArtworkReview: deleteFn } = artworkReviewActions

  return dispatch(deleteFn(artworkReview))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useArtworkReviewForm(
  artworkReview: Partial<ArtworkReviewModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const artworkReviewForm = useForm(
    defaultFormState,
    { entity: artworkReview, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [artworkReview.id, artworkReview.cache_key],
  )

  return {
    ...artworkReviewForm,
  }
}

export const useRelations = (artworkReview: Partial<ArtworkReviewModel> = {}) => {
  const { artwork_id, status_id, user_id } = artworkReview

  const {
    artworks, images, statuses, users,
  } = useSelector(reduxState => reduxState.entities)

  const artwork = artwork_id && artworks[artwork_id] ? artworks[artwork_id] : {}
  const status = status_id && statuses[status_id] ? statuses[status_id] : {}
  const user = user_id && users[user_id] ? users[user_id] : {}
  // @ts-expect-error TS2339
  const userImage = user.image_id && images[user.image_id] ? images[user.image_id] : {}

  return {
    artwork,
    status,
    user,
    userImage,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: ArtworkReviewRequestOptions,
}

function useArtworkReview(initEntity: Partial<ArtworkReviewModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: artworkReview }: { entity: ArtworkReviewModel} = useLatestEntity(initEntity, 'artworkReviews')
  const { id } = artworkReview

  const dispatch = useDispatch()

  const {
    artwork, status, user, userImage,
  } = useRelations(artworkReview)

  useReduxAction(
    'artworkReviews',
    'loadArtworkReview',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.artworkReviews)

  // @ts-expect-error TS2339
  const isApproved = status.key === 'approved'

  const createdDateString = digObject(artworkReview, 'dates.created.date_time')

  return {
    artwork,
    artworkReview,
    callbacks: {
      createArtworkReview: (
        artworkReviewParams: Partial<ArtworkReviewModel>,
        entityOptions?: ArtworkReviewRequestOptions,
      ) => (
        createArtworkReview({ artworkReviewParams, dispatch, requestOptions: entityOptions })
      ),
      deleteArtworkReview: () => deleteArtworkReview({ dispatch, artworkReview }),
      updateArtworkReview: (
        artworkReviewParams: Partial<ArtworkReviewModel>,
        entityOptions?: ArtworkReviewRequestOptions,
      ) => (
        updateArtworkReview({
          artworkReview, artworkReviewParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    createdDateString,
    creating,
    deleting,
    isApproved,
    loading: loading && !loadedIds.includes(artworkReview.id),
    status,
    updating,
    urls: generateUrls(artworkReview),
    user,
    userImage,
  }
}

export default useArtworkReview
